import { CSSProperties, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useGetCRMFeatureRequestDataByTPIDQuery, useGetCRMFeatureRequestDataQuery } from "../../slices/ExploreCustomers.slice";
import { IFeatureRequestSummary } from "../../interfaces/CloseTheLoop.interfaces";
import FeatureTable from "../FeatureRequest/FeatureTable";
import FeatureChart from "../FeatureRequest/FeatureChart";

export default function FeatureRequestContainer(props: any) {
  const [searchParams] = useSearchParams();
  // Chart filters as array to handle multiple simultaneous filters (e.g. product + status)
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);
  const tenantId = searchParams.get("id");
  // View mode driven by the Feature Request sub-tab: "tenant" (default) scopes to the current
  // tenant; "tpid" scopes to the customer's TPID (top parent id), spanning all its tenants.
  const isTpidView = props?.viewMode === "tpid";
  const tpid = props?.tpid ? String(props.tpid) : "";

  // Feature request data now comes from the Close The Loop feature evidences
  // (Fn_S360_CTL_getFeatureEvidences), served through the customer endpoint
  // (customer/getCRMFeatureRequestData) scoped to the current customer's tenant.
  // A single customer's set is bounded, so we fetch it in one call and paginate client-side.
  const tenantFeatureRequestData = useGetCRMFeatureRequestDataQuery(
    {
      tenantId: tenantId ?? "",
      page: 1,
      pageSize: 1000000,
      sortBy: "lastChangedDate",
      sortDirection: "desc"
    },
    { skip: isTpidView || !tenantId }
  );

  // TPID view uses the sibling endpoint (customer/getCRMFeatureRequestDataByTPID) which filters
  // evidences by an exact TPID match server-side.
  const tpidFeatureRequestData = useGetCRMFeatureRequestDataByTPIDQuery(
    {
      tpid,
      page: 1,
      pageSize: 1000000,
      sortBy: "lastChangedDate",
      sortDirection: "desc"
    },
    { skip: !isTpidView || !tpid }
  );

  const featureRequestData = isTpidView ? tpidFeatureRequestData : tenantFeatureRequestData;

  // Reset any chart-driven filters when the active view (tenant vs TPID) or the underlying
  // identifier changes, so a selection made in one sub-tab does not silently filter the other.
  useEffect(() => {
    setChartFilters([]);
  }, [isTpidView, tenantId, tpid]);

  const allRows: IFeatureRequestSummary[] = useMemo(
    () => featureRequestData?.data?.featureRequests ?? [],
    [featureRequestData?.data?.featureRequests]
  );

  // Apply chart-driven filters (client-side)
  const filteredRows = useMemo(() => {
    if (chartFilters.length === 0) {
      return allRows;
    }
    return allRows.filter((row) =>
      chartFilters.every((filter) => String((row as any)[filter.filterKey] ?? "") === filter.value)
    );
  }, [allRows, chartFilters]);

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Handler to manage the array of chart filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        return [...prevFilters, newFilter];
      }
      const updatedFilters = [...prevFilters];
      updatedFilters[existingFilterIndex] = newFilter;
      return updatedFilters;
    });
  };

  // In TPID view a customer may not have a top parent id; the TPID query is then skipped,
  // leaving featureRequestData in an uninitialized state (no loading/success/error). Guard
  // that path explicitly so we show a no-data message instead of a blank tab.
  const isMissingTpidInTpidView = isTpidView && !tpid;

  const showNoData = (message?: string) => {
    const rootStyle: CSSProperties = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return (
      <div style={rootStyle}>
        {message ?? `Feature request details are not available for ${props.tenantName}.`}
      </div>
    );
  };

  const HandleAPIExcpetion = () => {
    const enableAPIException = featureRequestData.error;
    return (enableAPIException && <DisplayAPIsException error={enableAPIException} height={250} />) || "";
  };

  // Hooks
  useEffect(() => {
    logEvent("Feature Request", "Customer Page", "FeatureRequestContainer", customProps);
  }, []);

  useEffect(() => {
    if (featureRequestData.error) {
      logException(
        "Customer Page - Feature Request Tab",
        "FeatureRequestContainer",
        "useGetCRMFeatureRequestDataQuery",
        featureRequestData.error,
        customProps
      );
    }
  }, [featureRequestData.error]);

  return (
    <>
      <FeatureChart
        loading={isMissingTpidInTpidView ? false : featureRequestData.isLoading}
        onSelectedChartFilter={handleChartFilter}
        tenantId={tenantId}
        tenantName={props.tenantName}
        bannerMessage="This view shows feature requests from all sources (CRM, UAT, CSS)."
        options={{
          filteredData: filteredRows,
          originalData: allRows,
          chartOptions: [
            {
              id: 1,
              field: "priority",
              key: "customer-view-feature-request-chart-by-priority",
              title: "Feature Request By Priority",
              type: "donut"
            },
            {
              id: 2,
              field: "productName",
              key: "customer-view-feature-request-chart-by-productName",
              title: "Feature Request By Product",
              type: "donut"
            },
            {
              id: 3,
              field: "status",
              key: "customer-view-feature-request-chart-by-status",
              title: "Feature Request By Feature Status",
              type: "donut"
            }
          ]
        }}
      />
      {isMissingTpidInTpidView ? (
        showNoData("Feature request details are not available because TPID is not set for this customer.")
      ) : featureRequestData.isLoading ? (
        <CustomShimmer columnCount={10} />
      ) : (
        (featureRequestData.isSuccess && allRows.length > 0 && (
          <div style={{ marginLeft: 8, marginRight: 8 }}>
            <FeatureTable
              rows={filteredRows}
              loading={featureRequestData.isFetching}
              tenantName={props.tenantName}
            />
          </div>
        )) ||
        (featureRequestData.isSuccess && allRows.length === 0 && showNoData())
      )}
      <HandleAPIExcpetion />
    </>
  );
}
